/**
 * Access model shared by every CoSparks resource (boards, retros, goal spaces).
 * See architecture/cosparks-design.md.
 */

export type ResourceType = 'board' | 'retro' | 'goalSpace';

export const RESOURCE_TYPES: readonly ResourceType[] = ['board', 'retro', 'goalSpace'];

/** One-letter code in every space link (`/b/:shareId`) and in wrap-up lists ("b.<shareId>"). */
export const RESOURCE_CODES: Record<ResourceType, 'b' | 'r' | 'g'> = { board: 'b', retro: 'r', goalSpace: 'g' };

/** What people call each type. */
export const RESOURCE_NAMES: Record<ResourceType, string> = { board: 'board', retro: 'retro', goalSpace: 'goal space' };

/** The client path of a space: `/b/<shareId>`. */
export const spacePath = (type: ResourceType, shareId: string) => `/${RESOURCE_CODES[type]}/${shareId}`;

/** Author / owner id written over an erased user's guid. */
export const ERASED_AUTHOR = 'erased';

/** Shown for authors who erased their account or can no longer be found. */
export const FORMER_USER = 'Former user';


/** Ordered from least to most privileged. */
export type ResourceRole = 'viewer' | 'contributor' | 'admin';

export const ROLE_RANK: Record<ResourceRole, number> = {
    viewer: 0,
    contributor: 1,
    admin: 2,
};

/** Free resources admit at most this many people at once, anonymous viewers included. The owner is always admitted. */
export const FREE_CONCURRENT_LIMIT = 6;
/** Premium resources admit at most this many people at once, counted the same way. */
export const PREMIUM_CONCURRENT_LIMIT = 50;

/** Maximum number of emails on a restricted resource. */
export const MAX_ALLOWED_EMAILS = 200;
/** People besides the owner who can run a space (facilitator actions and sharing). */
export const MAX_CO_FACILITATORS = 10;

/** Banner and board images: upload size, and the box the server shrinks them into. */
export const IMAGE_LIMITS = { maxBytes: 8 * 1024 * 1024, maxWidth: 2400, maxHeight: 1600 } as const;

/** A Persera admin's premium grant runs for at most this long. */
export const PREMIUM_GRANT_MAX_YEARS = 10;

/** Set when a Persera admin made a resource premium regardless of its owner's plan. */
export interface PremiumGrant {
    grantedBy: string;
    grantedAt: Date | string;
}

export interface ResourceAccess {
    ownerId: string;
    shareId: string;
    restricted: boolean;
    allowedEmails: string[];
    /**
     * Co-facilitators, by lowercased email: admins of the space like the owner (they get in even when it's
     * restricted), except that only the owner deletes the space or changes this list. Every tier.
     */
    adminEmails?: string[];
    /** Persera admin grant: premium, so PREMIUM_CONCURRENT_LIMIT people at once, whatever the owner's plan. */
    premiumGrant?: PremiumGrant | null;
    /**
     * The CoSparks Workspace the space was created in (set once, at creation). Only signed-in members of that
     * workspace get in, its owner included; the email list and co-facilitators apply on top. null/absent = personal.
     */
    workspaceId?: string | null;
    /**
     * Boards and retros: share id of the goal space the facilitator linked, shown in the space's Goals panel. Only
     * the link is kept: each viewer opens the goal space with their own access.
     */
    linkedGoalSpace?: string | null;
}

/** Why an account is premium. null = free. 'workspace': a member of a CoSparks Workspace. */
/** Why an account has Premium of its own. A workspace never gives it: facilitating makes only that workspace's spaces premium. */
export type PremiumSource = 'admin' | 'granted' | 'subscription' | 'trial' | null;

/** Workspace admins manage who is in; members can create spaces in it. Both are premium. */
export type WorkspaceRole = 'admin' | 'member';

/** A CoSparks Workspace the signed-in person belongs to. */
export interface WorkspaceRefDto {
    id: string;
    /** Its address: /w/<slug>. */
    slug: string;
    name: string;
    role: WorkspaceRole;
    /** A facilitator in it while it's premium: their spaces in it are premium (not their spaces elsewhere). */
    facilitator: boolean;
}

/** free: anyone's, organizing only. premium: paid for (a subscription, or by Persera on invoice), its facilitators are premium. */
export type WorkspaceTier = 'free' | 'premium';

/** How a premium workspace is paid for. */
export type WorkspaceBilling = 'subscription' | 'invoice';

/**
 * A workspace is paid up through the whole of its `paidUntil` day (UTC); with no date it never lapses. Billing is per
 * workspace: a company may have several, each with its own seats and period.
 */
export function workspacePaidUp(paidUntil: string | null | undefined, now = Date.now()): boolean {
    if (!paidUntil) return true;
    return now < Date.parse(`${paidUntil}T00:00:00Z`) + 24 * 60 * 60 * 1000;
}

/** Lowercase letters, digits and single hyphens, 2–40 characters: /w/<slug>. */
export const WORKSPACE_SLUG_RE = /^[a-z0-9](?:[a-z0-9]|-(?=[a-z0-9])){1,39}$/;

/** "Persera Software!" → "persera-software" (may be too short or empty: check WORKSPACE_SLUG_RE). */
export function slugify(name: string): string {
    return name.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/, '');
}

/**
 * Where the facilitator has sent everyone in a space next (e.g. from the board to the retro). Live event `next_up`
 * (null when cleared); kept in memory while the server runs.
 */
export interface NextUpDto {
    type: ResourceType;
    shareId: string;
    title: string;
    /** Display name of the facilitator who sent everyone there. */
    by: string;
}

/** What the client gets back about the caller's access to a resource. */
export interface AccessSummaryDto {
    type: ResourceType;
    shareId: string;
    role: ResourceRole;
    /** Premium now (granted, or its owner's plan or facilitator seat): PREMIUM_CONCURRENT_LIMIT people at once. */
    premium: boolean;
    /** A Persera admin made this resource premium. */
    premiumGranted: boolean;
    restricted: boolean;
    /** People at once: FREE_CONCURRENT_LIMIT, or PREMIUM_CONCURRENT_LIMIT when premium. */
    concurrentLimit: number | null;
    /** The caller created this space: only they delete it or change its co-facilitators. */
    isOwner: boolean;
    /** Admins only. */
    allowedEmails?: string[];
    /** Admins only: co-facilitators' emails. */
    adminEmails?: string[];
    /** The workspace the space is in: only its members can open it. null for personal spaces. */
    workspace: { id: string; slug: string; name: string } | null;
    /** Share id of the goal space linked to this board or retro (its Goals panel), or null. */
    linkedGoalSpace: string | null;
}

export interface UpdateAccessDto {
    restricted?: boolean;
    allowedEmails?: string[];
    /** Owner only: the full list of co-facilitators. */
    adminEmails?: string[];
    /** Link a goal space to this board or retro by its share id; null unlinks. */
    linkedGoalSpace?: string | null;
}

/** Sent with every create (boards, retros, goal spaces): where the space goes and who can open it. */
export interface NewSpaceAccessDto {
    /** A CoSparks Workspace the creator is in; null = personal. */
    workspaceId: string | null;
    /** Only the creator and `allowedEmails` can open it (and, in a workspace, they must be members). */
    restricted: boolean;
    allowedEmails: string[];
}

export interface PresenceEntry {
    /** user:<guid> or anon:<id>; anonymous ids are opaque. */
    participantId: string;
    displayName: string;
    isAnonymous: boolean;
    isOwner: boolean;
}

/** Error codes returned in `{ code }` bodies so the client can show the right screen. */
export type AccessErrorCode =
    | 'NOT_FOUND'          // 404: no such shareId, or link rotated
    | 'SIGN_IN_REQUIRED'   // 401: anonymous request needs a session (writes, restricted resources)
    | 'FORBIDDEN'          // 403: signed in, but not on the email list / not the admin
    | 'RESOURCE_FULL'      // 409: free resource already has FREE_CONCURRENT_LIMIT people
    | 'PREMIUM_REQUIRED'   // 402: action needs an active subscription
    | 'WORKSPACE_ONLY';    // 403: signed in, but not a member of the workspace the space is in

export interface AccessErrorBody {
    code: AccessErrorCode;
    message: string;
}

/**
 * A Persera admin acting as another user can do what they can, except erase their account or change
 * billing: those are refused with 403 { code: 'IMPERSONATION_NOT_ALLOWED' }. Kept apart from
 * AccessErrorCode, which drives the access screens.
 */
export type ImpersonationErrorCode = 'IMPERSONATION_NOT_ALLOWED';

export interface ImpersonationErrorBody {
    code: ImpersonationErrorCode;
    message: string;
}

/** A Persera admin is acting as this user. */
export interface ImpersonationDto {
    adminName: string;
    targetName: string;
    targetEmail: string;
    startedAt: string;
    expiresAt: string;
}

/** An open action item assigned to the signed-in person, on Home's "My actions". */
export interface MyActionItemDto {
    type: ResourceType;
    shareId: string;
    /** The board or retro it came from. */
    spaceTitle: string;
    id: string;
    text: string;
    createdAt: string;
    /** Experiment (run004): 'YYYY-MM-DD'. */
    dueDate?: string;
    link?: ExternalLink;
}

/**
 * The same work in another tool, e.g. the Jira ticket an action item became. On action items and goals; set through
 * MCP so a later sync can find what it created. Only http(s) URLs.
 */
export interface ExternalLink {
    url: string;
    /** Shown instead of the URL, e.g. "PROJ-123". */
    label?: string;
}

export const EXTERNAL_LINK_LIMITS = { url: 2000, label: 80 } as const;

/** Entry on the Home page lists (owned / shared with me). */
export interface ResourceListItemDto {
    type: ResourceType;
    shareId: string;
    title: string;
    role: ResourceRole;
    /** Premium now: same rule as the space's own page. */
    premium: boolean;
    restricted: boolean;
    ownerName: string;
    updatedAt: string;
    lastVisitedAt?: string;
    /** Only on the Archived list. */
    archived?: boolean;
    /** Set for spaces in a CoSparks Workspace. */
    workspaceName?: string;
    workspaceSlug?: string;
}

/** A passage of a resource's content that matched a search, trimmed around the match. */
export interface SearchSnippet {
    /** What it is: "Sticky note", "Card", "Goal", "Key result", "Comment"... */
    kind: string;
    /** The passage, possibly with "…" at either end. */
    text: string;
    /** Where the match is within `text`, for highlighting. */
    matchStart: number;
    matchLength: number;
    /** Appended to the resource's path to open the matching part, e.g. "?goal=<id>". */
    linkSuffix?: string;
}

/** One space on the search results page (GET /api/search). */
export interface SearchResultDto extends ResourceListItemDto {
    titleMatch: boolean;
    /** Matches in the content (all of them, not only the snippets shown). */
    contentMatches: number;
    snippets: SearchSnippet[];
}

export interface SearchResponseDto {
    query: string;
    results: SearchResultDto[];
    /** Content was searched (queries of 2+ characters); otherwise titles only. */
    searchedContent: boolean;
}

/** Features switched on for this user in this environment (see services/featureGates.ts). */
export interface FeatureGatesDto {
    /** Search inside spaces on /search. Off: titles only. */
    contentSearch: boolean;
}

export interface MeDto {
    guid: string;
    username: string;
    email: string;
    premium: boolean;
    /** Why premium: Persera admin, admin grant, or subscription. null when free. */
    premiumSource: PremiumSource;
    /** When premiumSource is 'granted': when the grant ends (ISO), or null for no end date. */
    premiumGrantExpiresAt?: string | null;
    /** When premiumSource is 'trial': when the CoSparks trial ends (ISO). */
    trialEndsAt?: string | null;
    /** The account can still start its one CoSparks trial (never had one, and isn't premium now). */
    trialAvailable: boolean;
    /** Persera admins can grant premium to users and spaces (see /admin). */
    isPerseraAdmin: boolean;
    /** CoSparks Workspaces the account is a member of (listed by email). */
    workspaces: WorkspaceRefDto[];
    features: FeatureGatesDto;
    subscription: SubscriptionSummaryDto;
    /** Set while a Persera admin is viewing as this user: everything is read-only. */
    impersonation?: ImpersonationDto | null;
}

export interface SubscriptionSummaryDto {
    active: boolean;
    isTrial: boolean;
    cancelled: boolean;
    expiryDate: string | null;
}

/** Persera admin tools (/api/admin). */
export interface AdminPremiumGrantDto {
    grantedBy: string;
    grantedAt: string;
    /** null = no end date. */
    expiresAt: string | null;
}

export interface AdminUserDto {
    guid: string;
    username: string;
    email: string;
    isPerseraAdmin: boolean;
    premiumSource: PremiumSource;
    /** Set when a Persera admin granted premium (it may have expired: compare expiresAt with now). */
    premiumGrant: AdminPremiumGrantDto | null;
    /** Names of the CoSparks Workspaces the user is a member of. */
    workspaceNames: string[];
    /** Those where they're a facilitator while it's premium: their spaces there are premium. */
    facilitatorIn: string[];
    /** The user's CoSparks subscription (from the hub's 60 s cache). */
    subscription: SubscriptionSummaryDto | null;
    /** First visit to CoSparks (ISO). */
    createdAt: string;
    /** Last visit, updated at most hourly (ISO); null before this was tracked. */
    lastSeenAt: string | null;
    /** Spaces owned, archived included. */
    owned: Record<ResourceType, number>;
}

export type AdminUserSort = 'joined' | 'lastSeen' | 'name' | 'email';

export interface AdminUserListDto {
    users: AdminUserDto[];
    total: number;
    page: number;
    pageSize: number;
}

/** Body of PUT /api/admin/users/:guid/premium. */
export interface GrantUserPremiumDto {
    granted: boolean;
    /** ISO date-time in the future, at most 10 years out; null or absent = no end date. */
    expiresAt?: string | null;
}

export type ImpersonationEndReason = 'stopped' | 'expired' | 'logout' | 'admin-revoked' | 'target-missing';

export interface ImpersonationLogDto {
    id: string;
    adminGuid: string;
    adminEmail: string;
    /** 'erased' after the target erased their account. */
    targetGuid: string;
    targetEmail: string | null;
    startedAt: string;
    expiresAt: string;
    endedAt: string | null;
    endedReason: ImpersonationEndReason | null;
    userAgent?: string;
    /** Changes made while acting as the target. */
    actionCount: number;
    /** The most recent changes, newest last (method, path, status; never content). */
    actions: ImpersonationActionDto[];
}

export interface ImpersonationActionDto {
    at: string;
    method: string;
    path: string;
    status: number;
}

export interface ImpersonationLogListDto {
    entries: ImpersonationLogDto[];
    total: number;
    page: number;
    pageSize: number;
}

export interface AdminResourceDto {
    type: ResourceType;
    shareId: string;
    title: string;
    ownerName: string;
    ownerEmail: string;
    /** Premium now: granted, or its owner's plan or facilitator seat. */
    premium: boolean;
    premiumGranted: boolean;
}

/** Someone listed in a workspace, by the email they sign in with. */
export interface WorkspaceMemberDto {
    email: string;
    role: WorkspaceRole;
    /** Holds one of the workspace's facilitator seats: premium while the workspace is. */
    seat: boolean;
    /** Their CoSparks name, once they have visited. */
    name: string | null;
    addedAt: string;
}

/** GET /api/workspaces/:id: for members, and for Persera admins. */
export interface WorkspaceDto {
    id: string;
    slug: string;
    name: string;
    /** The caller's role; null for a Persera admin who isn't a member. */
    role: WorkspaceRole | null;
    /** Add and remove members (workspace admins and Persera admins). */
    canManageMembers: boolean;
    /** Make people workspace admins or members again (Persera admins only). */
    canManageAdmins: boolean;
    /** Make members workspace admins (workspace admins and Persera admins); only Persera admins undo it. */
    canAddAdmins: boolean;
    tier: WorkspaceTier;
    members: WorkspaceMemberDto[];
    /** For whoever manages members: the plan, what it costs, and who ran sessions this month. */
    plan?: WorkspacePlanDto;
}

/** A workspace's plan as its admins see it (WORKSPACE_PRICING). */
export interface WorkspacePlanDto {
    tier: WorkspaceTier;
    /** How it's paid for, when premium. */
    billing: WorkspaceBilling | null;
    /** For a subscription: billed monthly or yearly. */
    interval: 'month' | 'year' | null;
    /** For a subscription: it ends at the end of the paid period instead of renewing. */
    cancelAtPeriodEnd: boolean;
    /** Facilitator seats paid for (at least the minimum when premium). */
    seats: number;
    /** Seats given to members. */
    used: number;
    perSeatMonthly: number;
    yearlyTotal: number;
    /** Last paid day, 'YYYY-MM-DD'; null = no end date. */
    paidUntil: string | null;
    active: boolean;
    /** Members who ran or created a space this calendar month (anyone listed may). */
    facilitatorsThisMonth: Array<{ email: string; name: string | null }>;
}

/** Someone on the workspace admins' email domain who uses CoSparks but isn't a member yet: one step to add. */
export interface WorkspaceSuggestionDto {
    email: string;
    name: string | null;
}

/** Body of POST /api/workspaces/:id/members. role 'admin' is for Persera admins only. */
export interface AddWorkspaceMembersDto {
    emails: string[];
    role?: WorkspaceRole;
}

/** A workspace on /admin. */
export interface AdminWorkspaceDto {
    id: string;
    slug: string;
    name: string;
    /** Facilitators it may have (a Persera admin's number, else what was bought). */
    seats: number;
    /** Set by a Persera admin: overrides what was bought. */
    seatOverride: number | null;
    tier: WorkspaceTier;
    billing: WorkspaceBilling | null;
    /** Made by someone from Home (not set up by a Persera admin). */
    selfServe: boolean;
    /** Invoice: last paid day, 'YYYY-MM-DD' (null = no end date). Subscription: when the paid period ends. */
    paidUntil: string | null;
    /** Paid up: its facilitators' spaces in it are premium (PREMIUM_CONCURRENT_LIMIT people at once). */
    active: boolean;
    memberCount: number;
    adminEmails: string[];
    /** Spaces created in it. */
    spaceCount: number;
    createdAt: string;
}

/** Body of POST /api/workspaces: anyone signed in starts a (free) workspace and is its admin. */
export interface CreateOwnWorkspaceDto {
    name: string;
}

/** Body of POST /api/workspaces/:slug/billing/checkout (workspace admins): buy (or change) facilitator seats. */
export interface WorkspaceCheckoutDto {
    interval: 'month' | 'year';
    /** At least WORKSPACE_PRICING.minimumSeats. */
    seats: number;
}

/** Body of PUT /api/workspaces/:slug/members/:email/seat (workspace admins). */
export interface WorkspaceSeatDto {
    seat: boolean;
}

/**
 * Pushed by the dSolver hub to POST /api/sub/workspace/:workspaceId whenever a workspace subscription changes
 * (see human.md: hub changes for workspace billing).
 */
export interface WorkspaceSubscriptionPush {
    active: boolean;
    interval: 'month' | 'year';
    /** Facilitator seats bought (the Stripe quantity). */
    seats: number;
    /** End of the paid period, ISO. */
    expiryDate: string;
    cancelAtPeriodEnd?: boolean;
    /** guid of who bought it. */
    purchasedBy?: string;
}

/** Body of POST /api/admin/workspaces: a name and its first workspace admins. */
export interface CreateWorkspaceDto {
    name: string;
    /** Its address, /w/<slug>; made from the name when absent. */
    slug?: string;
    adminEmails: string[];
    /** Seats it pays for; at least WORKSPACE_PRICING.minimumSeats. */
    seats?: number;
    /** Last paid day, 'YYYY-MM-DD'; null or absent = no end date. */
    paidUntil?: string | null;
}

/** Body of PATCH /api/admin/workspaces/:id. A new slug changes the workspace's address (old links stop working). */
export interface UpdateWorkspaceDto {
    name?: string;
    slug?: string;
    seats?: number;
    /** Last paid day, 'YYYY-MM-DD'; null = no end date. Set by Persera admins, any length. */
    paidUntil?: string | null;
    /** Persera admins: premium by invoice, or back to free. Not for a workspace on a subscription. */
    tier?: WorkspaceTier;
    /** Persera admins: this many facilitators whatever was bought; null = what was bought. */
    seatOverride?: number | null;
}
